/**
 * SVG import for Paper2Ink editor.
 *
 * SVG is an interchange format, never the canonical runtime (ARCH invariant 2):
 * imported SVG is parsed and converted into CIM scene objects; nothing is
 * mounted as live DOM and no DOM APIs are required at runtime.
 *
 * Supported subset (01_FULL_SPEC SVG-002): path (M/L/Z subset), rect, circle,
 * ellipse, line, polyline, polygon, g (transform composition), per-element
 * transform, viewBox (baked as translation), fill/stroke/opacity attributes.
 *
 * Sanitization (SVG-003): <script>, <foreignObject>, <image>, <use>, <iframe>
 * are removed; event handler attributes and javascript:/data: URLs are
 * stripped; children of unsafe links are dropped, not repaired. Deferred
 * elements (text) are reported, never silently dropped.
 */

import type { CIMDocument, CompoundFill, CenterlineStroke, Ring } from './model.ts';

export interface SVGImportStats {
  scriptsRemoved: number;
  unsafeAttrsRemoved: number;
  unsafeLinksRemoved: number;
}

export interface SkippedElement {
  tag: string;
  id: string | null;
  reason: string;
}

export interface SVGImportResult {
  objects: Array<CompoundFill | CenterlineStroke>;
  skipped: SkippedElement[];
  stats: SVGImportStats;
}

const SUPPORTED_SHAPE_TAGS = new Set([
  'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon',
]);
const REMOVED_TAGS = new Set(['script', 'foreignobject', 'image', 'use', 'iframe']);
const DEFERRED_TAGS = new Set(['text']);
const UNSAFE_URI_RE = /^\s*(javascript|data)\s*:/i;
const EVENT_ATTR_RE = /^on[a-z]+$/i;

// ---------------------------------------------------------------------------
// Minimal XML parsing (no DOM dependency: runs in browser and bare node)
// ---------------------------------------------------------------------------

interface XNode {
  tag: string;
  attrs: Map<string, string>;
  children: XNode[];
  parent: XNode | null;
}

function unescapeXml(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#x?[0-9a-fA-F]+;/g, (m) => {
      if (m.startsWith('&#x')) return String.fromCodePoint(parseInt(m.slice(3, -1), 16));
      return String.fromCodePoint(parseInt(m.slice(2, -1), 10));
    })
    .replace(/&amp;/g, '&');
}

function parseXML(svgText: string): XNode {
  const sentinel: XNode = { tag: '#sentinel', attrs: new Map(), children: [], parent: null };
  let current = sentinel;
  let i = 0;
  const n = svgText.length;
  while (i < n) {
    if (svgText.startsWith('<!--', i)) {
      const end = svgText.indexOf('-->', i + 4);
      if (end === -1) throw new Error('SVG import failed: malformed XML (unterminated comment)');
      i = end + 3;
    } else if (svgText.startsWith('<![CDATA[', i)) {
      const end = svgText.indexOf(']]>', i + 9);
      if (end === -1) throw new Error('SVG import failed: malformed XML (unterminated CDATA)');
      i = end + 3;
    } else if (svgText.startsWith('<?', i) || svgText.startsWith('<!', i)) {
      const end = svgText.indexOf('>', i + 2);
      if (end === -1) throw new Error('SVG import failed: malformed XML (unterminated declaration)');
      i = end + 1;
    } else if (svgText[i] === '<') {
      if (svgText[i + 1] === '/') {
        const end = svgText.indexOf('>', i);
        if (end === -1) throw new Error('SVG import failed: malformed XML (unterminated closing tag)');
        const name = svgText.slice(i + 2, end).trim().toLowerCase();
        if (current.tag !== name) {
          throw new Error(`SVG import failed: malformed XML (mismatched closing tag </${name}>, open <${current.tag}>)`);
        }
        current = current.parent ?? sentinel;
        i = end + 1;
      } else {
        const tagMatch = /^<\s*([A-Za-z_][\w.:-]*)/.exec(svgText.slice(i));
        if (!tagMatch) throw new Error('SVG import failed: malformed XML (invalid tag)');
        const tag = tagMatch[1].toLowerCase();
        // Find end of the open tag, honoring quoted attribute values.
        let j = i + tagMatch[0].length;
        let selfClosing = false;
        let end = -1;
        let quote: string | null = null;
        for (; j < n; j++) {
          const ch = svgText[j];
          if (quote !== null) {
            if (ch === quote) quote = null;
          } else if (ch === '"' || ch === "'") {
            quote = ch;
          } else if (ch === '>') {
            end = j;
            break;
          } else if (ch === '/' && svgText[j + 1] === '>') {
            end = j;
            selfClosing = true;
            break;
          }
        }
        if (end === -1) throw new Error(`SVG import failed: malformed XML (unclosed tag <${tag}>)`);
        const attrText = svgText.slice(i + tagMatch[0].length, end);
        const node: XNode = { tag, attrs: new Map(), children: [], parent: current };
        const attrRe = /([A-Za-z_:@][\w.:-]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
        let m: RegExpExecArray | null;
        while ((m = attrRe.exec(attrText)) !== null) {
          node.attrs.set(m[1].toLowerCase(), unescapeXml(m[2] ?? m[3] ?? ''));
        }
        current.children.push(node);
        if (!selfClosing) current = node;
        i = end + 1;
      }
    } else {
      // Text content: ignored (shapes carry no semantics in text nodes).
      const next = svgText.indexOf('<', i);
      i = next === -1 ? n : next;
    }
  }
  if (current !== sentinel) {
    throw new Error(`SVG import failed: malformed XML (unclosed tag <${current.tag}>)`);
  }
  const root = sentinel.children.find((c) => c.tag !== '#sentinel');
  if (!root || root.tag !== 'svg') {
    throw new Error(`SVG import failed: root element is '${root?.tag ?? 'none'}', expected 'svg'`);
  }
  return root;
}

// ---------------------------------------------------------------------------
// Affine transform handling
// ---------------------------------------------------------------------------

interface Affine {
  a: number; b: number; c: number; d: number; e: number; f: number;
}

const IDENTITY: Affine = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

/** Compose m1 ∘ m2 (apply m2 first, then m1). */
function compose(m1: Affine, m2: Affine): Affine {
  return {
    a: m1.a * m2.a + m1.c * m2.b,
    b: m1.b * m2.a + m1.d * m2.b,
    c: m1.a * m2.c + m1.c * m2.d,
    d: m1.b * m2.c + m1.d * m2.d,
    e: m1.a * m2.e + m1.c * m2.f + m1.e,
    f: m1.b * m2.e + m1.d * m2.f + m1.f,
  };
}

function translation(tx: number, ty: number): Affine {
  return { a: 1, b: 0, c: 0, d: 1, e: tx, f: ty };
}

function parseTransformAttr(value: string): Affine {
  let m: Affine = { ...IDENTITY };
  const re = /(matrix|translate|scale|rotate)\s*\(([^)]*)\)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(value)) !== null) {
    const args = match[2].split(/[\s,]+/).filter((s) => s.length > 0).map(Number);
    if (args.some((v) => !Number.isFinite(v))) continue;
    switch (match[1]) {
      case 'matrix': {
        if (args.length !== 6) break;
        m = compose({ a: args[0], b: args[1], c: args[2], d: args[3], e: args[4], f: args[5] }, m);
        break;
      }
      case 'translate':
        m = compose(translation(args[0] ?? 0, args[1] ?? 0), m);
        break;
      case 'scale': {
        const sx = args[0] ?? 1;
        m = compose({ a: sx, b: 0, c: 0, d: args[1] ?? sx, e: 0, f: 0 }, m);
        break;
      }
      case 'rotate': {
        const rad = ((args[0] ?? 0) * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const rot: Affine = { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
        if (args.length === 3) {
          // rotate(a, cx, cy) = T(cx,cy) ∘ R(a) ∘ T(−cx,−cy)
          let centered = compose(rot, translation(-args[1], -args[2]));
          centered = compose(translation(args[1], args[2]), centered);
          m = compose(centered, m);
        } else {
          m = compose(rot, m);
        }
        break;
      }
    }
  }
  return m;
}

function viewBoxToTranslation(root: XNode): Affine {
  const vbAttr = root.attrs.get('viewbox');
  if (!vbAttr) return { ...IDENTITY };
  const parts = vbAttr.split(/[\s,]+/).filter((s) => s.length > 0).map(Number);
  if (parts.length !== 4 || parts.some((v) => !Number.isFinite(v))) return { ...IDENTITY };
  // Bake the viewBox origin as a base translation. Width/height mapping into
  // page space is a placement concern handled outside the importer.
  return translation(-parts[0], -parts[1]);
}

// ---------------------------------------------------------------------------
// Sanitization
// ---------------------------------------------------------------------------

interface SanitizeStats {
  stats: SVGImportStats;
  dropped: Set<XNode>;
}

function sanitizeTree(root: XNode): SanitizeStats {
  const stats: SVGImportStats = { scriptsRemoved: 0, unsafeAttrsRemoved: 0, unsafeLinksRemoved: 0 };
  const dropped = new Set<XNode>();

  const stripAttrs = (node: XNode): void => {
    for (const [name, value] of Array.from(node.attrs.entries())) {
      const isEvent = EVENT_ATTR_RE.test(name);
      const isUnsafeUrl =
        (name === 'href' || name === 'xlink:href' || name === 'src') && UNSAFE_URI_RE.test(value);
      if (isEvent || isUnsafeUrl) {
        node.attrs.delete(name);
        stats.unsafeAttrsRemoved += 1;
      }
    }
  };

  const walk = (node: XNode): void => {
    const tag = node.tag;
    if (REMOVED_TAGS.has(tag)) {
      dropped.add(node);
      if (tag === 'script') stats.scriptsRemoved += 1;
      return; // do not descend: removed subtrees are gone entirely
    }
    if (tag === 'a') {
      const href = node.attrs.get('href') ?? node.attrs.get('xlink:href') ?? '';
      if (UNSAFE_URI_RE.test(href)) {
        stats.unsafeLinksRemoved += 1;
        dropped.add(node);
        const markSubtree = (child: XNode): void => {
          dropped.add(child);
          for (const g of child.children) markSubtree(g);
        };
        for (const child of node.children) markSubtree(child);
        return;
      }
    }
    stripAttrs(node);
    for (const child of node.children) walk(child);
  };

  for (const child of root.children) walk(child);
  return { stats, dropped };
}

// ---------------------------------------------------------------------------
// Shape conversion
// ---------------------------------------------------------------------------

type Pt = [number, number];

interface ParsedShape {
  kind: 'fill' | 'stroke';
  rings?: Pt[][];
  points?: Pt[];
  fillColor?: string;
  fillOpacity?: number;
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWidth?: number;
}

function numAttr(node: XNode, name: string, fallback: number): number {
  const v = node.attrs.get(name);
  if (v === undefined) return fallback;
  const parsed = Number(v);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function hexColorAttr(node: XNode, names: string[]): string | undefined {
  for (const name of names) {
    const v = node.attrs.get(name);
    if (v && /^#[0-9a-fA-F]{6}$/.test(v.trim())) return v.trim();
  }
  return undefined;
}

function approximateEllipse(cx: number, cy: number, rx: number, ry: number): Pt[] {
  const pts: Pt[] = [];
  const segments = 32;
  for (let k = 0; k < segments; k++) {
    const theta = (2 * Math.PI * k) / segments;
    pts.push([cx + rx * Math.cos(theta), cy + ry * Math.sin(theta)]);
  }
  return pts;
}

function parsePointsAttr(value: string): Pt[] {
  const tokens = value.split(/[\s,]+/).filter((s) => s.length > 0).map(Number);
  const pts: Pt[] = [];
  for (let k = 0; k + 1 < tokens.length; k += 2) {
    if (Number.isFinite(tokens[k]) && Number.isFinite(tokens[k + 1])) pts.push([tokens[k], tokens[k + 1]]);
  }
  return pts;
}

interface PathParseResult {
  ok: boolean;
  rings: Pt[][];
  openPoints: Pt[];
}

/**
 * Parse the M/L/Z subset of SVG path data (absolute and relative).
 * Unsupported commands (curves, arcs) degrade gracefully: ok=false and the
 * path is reported as skipped rather than half-imported.
 */
function parsePathData(d: string): PathParseResult {
  const result: PathParseResult = { ok: true, rings: [], openPoints: [] };
  const segmentRe = /([MmLlZzHhVvCcSsQqTtAa])([^MmLlZzHhVvCcSsQqTtAa]*)/g;
  let current: Pt[] = [];
  let cx = 0;
  let cy = 0;
  let startX = 0;
  let startY = 0;
  const closeRing = () => {
    if (current.length > 0) {
      result.rings.push(current);
      current = [];
    }
  };
  let match: RegExpExecArray | null;
  while ((match = segmentRe.exec(d)) !== null) {
    const cmd = match[1];
    const rawArgs = match[2].trim().length === 0 ? [] : match[2].trim().split(/[\s,]+/).map(Number);
    if (rawArgs.some((v) => !Number.isFinite(v))) {
      result.ok = false;
      break;
    }
    const rel = cmd >= 'a' && cmd <= 'z';
    switch (cmd.toLowerCase()) {
      case 'm': {
        if (rawArgs.length < 2 || rawArgs.length % 2 !== 0) {
          result.ok = false;
        } else {
          closeRing();
          cx = rel ? cx + rawArgs[0] : rawArgs[0];
          cy = rel ? cy + rawArgs[1] : rawArgs[1];
          startX = cx;
          startY = cy;
          current.push([cx, cy]);
          // Implicit lineto continuations after the initial moveto pair.
          for (let k = 2; k + 1 < rawArgs.length; k += 2) {
            cx = rel ? cx + rawArgs[k] : rawArgs[k];
            cy = rel ? cy + rawArgs[k + 1] : rawArgs[k + 1];
            current.push([cx, cy]);
          }
        }
        break;
      }
      case 'l': {
        if (rawArgs.length < 2 || rawArgs.length % 2 !== 0) {
          result.ok = false;
          break;
        }
        for (let k = 0; k + 1 < rawArgs.length; k += 2) {
          cx = rel ? cx + rawArgs[k] : rawArgs[k];
          cy = rel ? cy + rawArgs[k + 1] : rawArgs[k + 1];
          current.push([cx, cy]);
        }
        break;
      }
      case 'z': {
        if (current.length > 0) {
          // Explicit closure to subpath start for geometric exactness.
          current.push([startX, startY]);
          closeRing();
        }
        break;
      }
      default:
        // Curves/arcs are outside the SVG-002 first subset: degrade, do not guess.
        result.ok = false;
        break;
    }
    if (!result.ok) break;
  }
  closeRing();
  for (const ring of result.rings) result.openPoints.push(...ring.filter((p) => p !== undefined));
  return result;
}

function shapeToParsed(node: XNode): ParsedShape | null {
  const tag = node.tag;
  const fill = hexColorAttr(node, ['fill']);
  const stroke = hexColorAttr(node, ['stroke']);
  const strokeOpacity = node.attrs.has('stroke-opacity') ? Number(node.attrs.get('stroke-opacity')) : undefined;
  const fillOpacity = node.attrs.has('fill-opacity') ? Number(node.attrs.get('fill-opacity')) : undefined;
  const strokeWidth = node.attrs.has('stroke-width') ? Number(node.attrs.get('stroke-width')) : undefined;

  switch (tag) {
    case 'path': {
      const parsed = parsePathData(node.attrs.get('d') ?? '');
      if (!parsed.ok) return null;
      if (fill && parsed.rings.length > 0) {
        return { kind: 'fill', rings: parsed.rings, fillColor: fill, fillOpacity };
      }
      if (parsed.openPoints.length >= 2) {
        return { kind: 'stroke', points: parsed.openPoints, strokeColor: stroke ?? fill, strokeWidth, strokeOpacity };
      }
      return null;
    }
    case 'rect': {
      const x = numAttr(node, 'x', 0);
      const y = numAttr(node, 'y', 0);
      const w = numAttr(node, 'width', 0);
      const h = numAttr(node, 'height', 0);
      if (w <= 0 || h <= 0) return null;
      return {
        kind: 'fill',
        rings: [[[x, y], [x + w, y], [x + w, y + h], [x, y + h]]],
        fillColor: fill ?? '#000000',
        fillOpacity,
      };
    }
    case 'circle': {
      const r = numAttr(node, 'r', 0);
      if (r <= 0) return null;
      return {
        kind: 'fill',
        rings: [approximateEllipse(numAttr(node, 'cx', 0), numAttr(node, 'cy', 0), r, r)],
        fillColor: fill ?? '#000000',
        fillOpacity,
      };
    }
    case 'ellipse': {
      const rx = numAttr(node, 'rx', 0);
      const ry = numAttr(node, 'ry', 0);
      if (rx <= 0 || ry <= 0) return null;
      return {
        kind: 'fill',
        rings: [approximateEllipse(numAttr(node, 'cx', 0), numAttr(node, 'cy', 0), rx, ry)],
        fillColor: fill ?? '#000000',
        fillOpacity,
      };
    }
    case 'line': {
      return {
        kind: 'stroke',
        points: [
          [numAttr(node, 'x1', 0), numAttr(node, 'y1', 0)],
          [numAttr(node, 'x2', 0), numAttr(node, 'y2', 0)],
        ],
        strokeColor: stroke ?? '#000000',
        strokeWidth,
        strokeOpacity,
      };
    }
    case 'polyline':
    case 'polygon': {
      const pts = parsePointsAttr(node.attrs.get('points') ?? '');
      if (pts.length < 2) return null;
      if (tag === 'polygon') {
        return { kind: 'fill', rings: [pts], fillColor: fill ?? '#000000', fillOpacity };
      }
      return { kind: 'stroke', points: pts, strokeColor: stroke ?? fill, strokeWidth, strokeOpacity };
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

function makeId(idAttr: string | null | undefined, fallbackTag: string, used: Set<string>): string {
  let base = idAttr && idAttr.length > 0 ? idAttr : `svg_${fallbackTag}`;
  base = base.replace(/[^A-Za-z0-9_\-]/g, '_');
  let candidate = base;
  let k = 1;
  while (used.has(candidate)) {
    candidate = `${base}_${k}`;
    k += 1;
  }
  used.add(candidate);
  return candidate;
}

/**
 * Parse an SVG string into CIM objects ready to be appended to
 * `targetDoc.objects` (typically inside an AddObjectsCommand). Existing object
 * ids in the target document are respected: colliding ids get a suffix.
 */
export function importSVG(svgText: string, targetDoc: CIMDocument): SVGImportResult {
  const root = parseXML(svgText);
  const { stats, dropped } = sanitizeTree(root);
  const skipped: SkippedElement[] = [];
  const objects: Array<CompoundFill | CenterlineStroke> = [];
  const usedIds = new Set<string>(targetDoc.objects.map((o) => o.id));
  const base = viewBoxToTranslation(root);

  const walk = (node: XNode, frame: Affine): void => {
    if (dropped.has(node)) {
      skipped.push({ tag: node.tag, id: node.attrs.get('id') ?? null, reason: 'removed: unsafe element' });
      return;
    }
    const tag = node.tag;

    if (tag === 'g') {
      const tAttr = node.attrs.get('transform');
      const childFrame = compose(frame, tAttr !== undefined ? parseTransformAttr(tAttr) : { ...IDENTITY });
      for (const child of node.children) walk(child, childFrame);
      return;
    }
    if (DEFERRED_TAGS.has(tag)) {
      skipped.push({
        tag,
        id: node.attrs.get('id') ?? null,
        reason: 'deferred: text objects require a CIM contract extension (ADR)',
      });
      return;
    }
    if (!SUPPORTED_SHAPE_TAGS.has(tag)) {
      skipped.push({ tag, id: node.attrs.get('id') ?? null, reason: 'unsupported element outside the SVG-002 subset' });
      return;
    }

    const tAttr = node.attrs.get('transform');
    const local = compose(frame, tAttr !== undefined ? parseTransformAttr(tAttr) : { ...IDENTITY });
    const world = compose(base, local);
    const parsed = shapeToParsed(node);
    if (!parsed) {
      skipped.push({ tag, id: node.attrs.get('id') ?? null, reason: 'shape degraded: unsupported or empty geometry' });
      return;
    }

    const id = makeId(node.attrs.get('id'), tag, usedIds);
    const transform: [number, number, number, number, number, number] = [
      world.a, world.b, world.c, world.d, world.e, world.f,
    ];
    // Web model re-export shim: keep runtime type-checking in sync with the
    // CIM contract ('imported' is valid for both object kinds post ADR-0005).
    if (parsed.kind === 'fill') {
      const rings: Ring[] = (parsed.rings ?? []).map((pts) => ({ points: pts }));
      objects.push({
        id,
        kind: 'compound_fill',
        rings,
        fill_rule: 'nonzero',
        color: parsed.fillColor ?? '#000000',
        opacity: clampOpacity(parsed.fillOpacity),
        transform,
        group_id: null,
        source_kind: 'imported',
      } satisfies CompoundFill);
    } else {
      objects.push({
        id,
        kind: 'centerline_stroke',
        points: parsed.points ?? [],
        width: parsed.strokeWidth !== undefined && parsed.strokeWidth > 0 ? parsed.strokeWidth : 1,
        color: parsed.strokeColor ?? '#000000',
        opacity: clampOpacity(parsed.strokeOpacity),
        transform,
        pressure: null,
        timestamps: null,
        group_id: null,
        source_kind: 'imported',
      } satisfies CenterlineStroke);
    }
  };

  for (const child of root.children) walk(child, { ...IDENTITY });
  return { objects, skipped, stats };
}

function clampOpacity(v: number | undefined): number {
  if (v === undefined || !Number.isFinite(v)) return 1;
  return Math.min(1, Math.max(0, v));
}
